import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../Loader/LoadingSpinner";
import {
  ArrowDownToLine,
  Check,
  ChevronLeft,
  ChevronRight,
  Heart,
  Share2,
  X,
} from "lucide-react";
import API_UTIL from "../../services/AuthIntereptor";
import styled from "styled-components";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  grid-template-rows: 56px minmax(0, 1fr) auto auto;
  background:
    radial-gradient(circle at 50% 44%, rgba(37, 99, 235, 0.18), transparent 38%),
    #020617;
  color: #f8fafc;
  overflow: hidden;
`;

const Header = styled.header`
  display: grid;
  grid-template-columns: minmax(44px, 1fr) auto minmax(44px, 1fr);
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  background: rgba(15, 23, 42, 0.82);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
`;

const HeaderLeft = styled.div`
  display: flex;
  justify-content: flex-start;
  min-width: 0;
`;

const HeaderCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  line-height: 1.15;
`;

const HeaderRight = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  min-width: 0;
`;

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: ${(props) => (props.$active ? "rgba(244, 63, 94, 0.16)" : "rgba(255, 255, 255, 0.05)")};
  color: ${(props) => (props.$active ? "#fb7185" : "#cbd5e1")};
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease, color 150ms ease, opacity 150ms ease;

  &:hover {
    background: ${(props) => (props.$active ? "rgba(244, 63, 94, 0.2)" : "rgba(255, 255, 255, 0.1)")};
    border-color: ${(props) => (props.$active ? "rgba(244, 63, 94, 0.42)" : "rgba(255, 255, 255, 0.16)")};
    color: ${(props) => (props.$active ? "#fb7185" : "#ffffff")};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  border: 0;
  background: transparent;
  color: #cbd5e1;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  transition: color 150ms ease;

  &:hover {
    color: #ffffff;
  }

  .back-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 520px) {
    .back-label {
      display: none;
    }
  }
`;

const Counter = styled.div`
  color: #64748b;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 13px;
  letter-spacing: 0;
  white-space: nowrap;

  strong {
    color: #ffffff;
    font-weight: 700;
  }
`;

const FileName = styled.div`
  margin-top: 3px;
  max-width: min(34vw, 360px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #64748b;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 11px;
  letter-spacing: 0;

  @media (max-width: 640px) {
    display: none;
  }
`;

const Stage = styled.main`
  position: relative;
  display: grid;
  place-items: center;
  min-height: 0;
  padding: 24px 72px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 20px 52px;
  }

  @media (max-width: 520px) {
    padding: 16px 16px 72px;
  }
`;

const Glow = styled.div`
  position: absolute;
  inset: -20%;
  background: radial-gradient(circle at 50% 50%, hsla(${(props) => props.$hue}, 76%, 52%, 0.22), transparent 52%);
  pointer-events: none;
`;

const ImageFrame = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
`;

const ViewerImage = styled.img`
  && {
    display: block;
    max-width: min(100%, 1180px);
    max-height: calc(100vh - 232px);
    padding: 0;
    padding-top: 0;
    position: static;
    object-fit: contain;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    box-shadow: 0 24px 70px -20px rgba(0, 0, 0, 0.78);
    background: #0f172a;
  }

  @media (max-width: 768px) {
    max-height: calc(100vh - 220px);
  }

  @media (max-width: 520px) {
    max-width: 100%;
    max-height: calc(100vh - 236px);
  }
`;

const ImageVignette = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 8px;
  box-shadow: inset 0 0 80px rgba(0, 0, 0, 0.22);
  pointer-events: none;
`;

const FloatingArrow = styled.button`
  position: absolute;
  z-index: 2;
  top: 50%;
  ${(props) => (props.$side === "left" ? "left: 24px;" : "right: 24px;")}
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(15, 23, 42, 0.72);
  color: #ffffff;
  display: grid;
  place-items: center;
  cursor: pointer;
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.26);
  transition: background 150ms ease, transform 150ms ease, opacity 150ms ease;

  &:hover {
    background: rgba(15, 23, 42, 0.95);
    transform: translateY(-50%) scale(1.03);
  }

  &:disabled {
    opacity: 0;
    pointer-events: none;
  }

  svg {
    width: 22px;
    height: 22px;
  }

  @media (max-width: 640px) {
    ${(props) => (props.$side === "left" ? "left: 12px;" : "right: 12px;")}
    width: 40px;
    height: 40px;
  }

  @media (max-width: 520px) {
    top: auto;
    bottom: 14px;
    transform: none;

    &:hover {
      transform: scale(1.03);
    }
  }
`;

const ThumbnailRail = styled.nav`
  min-height: 72px;
  padding: 10px 48px;
  overflow-x: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(15, 23, 42, 0.68);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 640px) {
    padding: 9px 16px;
  }
`;

const ThumbButton = styled.button`
  flex: 0 0 auto;
  width: 54px;
  height: 54px;
  padding: 0;
  border-radius: 6px;
  border: 2px solid ${(props) => (props.$active ? "#3b82f6" : "rgba(255, 255, 255, 0.12)")};
  box-shadow: ${(props) => (props.$active ? "0 0 0 2px rgba(59, 130, 246, 0.28)" : "none")};
  opacity: ${(props) => (props.$active ? 1 : 0.62)};
  overflow: hidden;
  background: #111827;
  cursor: pointer;
  transition: opacity 150ms ease, border-color 150ms ease, box-shadow 150ms ease;

  &:hover {
    opacity: 1;
    border-color: ${(props) => (props.$active ? "#3b82f6" : "rgba(255, 255, 255, 0.32)")};
  }

  && img {
    width: 100%;
    height: 100%;
    max-width: none;
    max-height: none;
    padding: 0;
    padding-top: 0;
    position: static;
    object-fit: cover;
    display: block;
  }
`;

const FooterBar = styled.footer`
  min-height: 60px;
  padding: 10px 24px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  background: rgba(15, 23, 42, 0.95);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);

  @media (max-width: 767px) {
    grid-template-columns: 1fr auto;
    padding: 10px 16px;
  }
`;

const FooterNav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  @media (max-width: 767px) {
    display: none;
  }
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const FooterHint = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: #64748b;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 11px;
  white-space: nowrap;

  kbd {
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.06);
    color: #94a3b8;
    font: inherit;
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const TextNavButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 0;
  background: transparent;
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 4px;
  transition: color 150ms ease, opacity 150ms ease;

  &:hover {
    color: #ffffff;
  }

  &:disabled {
    color: #475569;
    cursor: not-allowed;
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

const ToolbarButton = styled.button`
  min-width: 42px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.06);
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease, color 150ms ease, opacity 150ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
  }

  @media (max-width: 520px) {
    padding: 0;

    span {
      display: none;
    }
  }
`;

const Toast = styled.div`
  position: fixed;
  left: 50%;
  bottom: 82px;
  z-index: 1002;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: #0f172a;
  color: #ffffff;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;

  svg {
    width: 16px;
    height: 16px;
    color: #34d399;
  }
`;

const EmptyState = styled.div`
  z-index: 1;
  display: grid;
  place-items: center;
  width: min(78vw, 560px);
  aspect-ratio: 4 / 3;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(15, 23, 42, 0.72);
  color: #94a3b8;
  font-weight: 700;
`;

const getImageSrc = (image) => image?.thumbnail || image?.s3_url || image?.original || "";
const getImageDisplaySrc = (image) => image?.original || image?.s3_url || image?.thumbnail || "";
const getImageOriginal = (image) => image?.original || image?.s3_url || image?.thumbnail || "";
const getImageFavourite = (image) =>
  Boolean(image?.isFavourites ?? image?.isFavourite ?? image?.is_favourite ?? false);

const formatNumber = (value) => String(value).padStart(2, "0");

const getFileName = (url) => {
  if (!url) return "Image";
  const cleanUrl = url.split("?")[0];
  const name = cleanUrl.split("/").pop();
  return decodeURIComponent(name || "Image");
};

const getHue = (value) => {
  const input = value || "imageshield";
  let hash = 0;
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 31 + input.charCodeAt(index)) % 360;
  }
  return hash;
};

const ImageModal = ({
  clickedImg,
  clickedImgIndex,
  setClickedImg,
  clickedUrl,
  handleFavourite,
  clickedImgFavourite,
  images = [],
  favourite = true,
  sharing = true,
  close = true,
  select = false,
}) => {
  const history = useNavigate();
  const initialIndex = Number.isInteger(clickedImgIndex) ? clickedImgIndex : 0;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFavourite, setIsFavourite] = useState(Boolean(clickedImgFavourite));
  const [imageLoaded, setImageLoaded] = useState(false);
  const [toast, setToast] = useState("");
  const touchStartRef = useRef(0);
  const thumbRefs = useRef([]);
  const toastTimerRef = useRef(null);
  // Holds a preloaded shareable File for the active image (iOS "Save to Photos").
  const preloadedShareRef = useRef({ url: null, file: null });

  const safeImages = useMemo(() => (Array.isArray(images) ? images : []), [images]);
  const hasImages = safeImages.length > 0;
  const activeImage = hasImages ? safeImages[currentIndex] || safeImages[0] : null;
  const activeSrc = getImageDisplaySrc(activeImage) || clickedImg;
  const activeOriginal = getImageOriginal(activeImage) || clickedUrl || clickedImg;
  const activeFileName = getFileName(activeOriginal || activeSrc);
  const activeHue = getHue(activeOriginal || activeSrc);

  // iOS Safari can only add an image to Photos via the native share sheet, and
  // navigator.share() must fire synchronously on tap (no network await in between).
  // So on iOS we PRELOAD the downloadable file when a photo is shown; the Download
  // tap then shares it instantly. Android/desktop never preload (they download directly).
  const isIOSDevice =
    typeof navigator !== "undefined" &&
    (/iP(hone|ad|od)/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

  useEffect(() => {
    if (!isIOSDevice || !activeOriginal || !navigator.canShare) return;
    let cancelled = false;
    preloadedShareRef.current = { url: activeOriginal, file: null };
    (async () => {
      try {
        const response = await API_UTIL.post("/downloadImage", { imageUrl: activeOriginal });
        if (cancelled || response.status !== 200) return;
        const blob = await (await fetch(response.data)).blob();
        const name = /\.(jpe?g|png|heic|webp)$/i.test(activeFileName || "")
          ? activeFileName
          : `${activeFileName || "photo"}.jpg`;
        const file = new File([blob], name, { type: blob.type || "image/jpeg" });
        if (!cancelled) preloadedShareRef.current = { url: activeOriginal, file };
      } catch (e) {
        // Preload failed — the Download button will fall back to a normal download.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOriginal]);
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < safeImages.length - 1;

  useEffect(() => {
    const propIndex = Number.isInteger(clickedImgIndex) ? clickedImgIndex : -1;
    const clickedImageIndex = safeImages.findIndex((image) => {
      const src = getImageSrc(image);
      const original = getImageOriginal(image);
      return src === clickedImg || original === clickedImg;
    });
    const clickedUrlIndex = safeImages.findIndex((image) => getImageOriginal(image) === clickedUrl);
    const matchedIndex = clickedImageIndex >= 0 ? clickedImageIndex : clickedUrlIndex;
    const nextIndex = matchedIndex >= 0 ? matchedIndex : propIndex >= 0 ? propIndex : 0;
    setCurrentIndex(Math.min(nextIndex, Math.max(safeImages.length - 1, 0)));
  }, [clickedImg, clickedImgIndex, clickedUrl, safeImages]);

  useEffect(() => {
    setImageLoaded(false);
    setIsFavourite(activeImage ? getImageFavourite(activeImage) : Boolean(clickedImgFavourite));
  }, [activeImage, activeSrc, clickedImgFavourite]);

  useEffect(() => {
    thumbRefs.current[currentIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [currentIndex]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  const showToast = (message) => {
    setToast(message);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(""), 1400);
  };

  const goToIndex = (nextIndex) => {
    if (!safeImages[nextIndex]) return;
    setCurrentIndex(nextIndex);
    setClickedImg(getImageSrc(safeImages[nextIndex]));
  };

  const handleNext = () => {
    if (canGoNext) goToIndex(currentIndex + 1);
  };

  const handlePrev = () => {
    if (canGoPrev) goToIndex(currentIndex - 1);
  };

  const handleClose = (event) => {
    event?.stopPropagation();
    setClickedImg(null);
    history(-1);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") handlePrev();
      if (event.key === "ArrowRight") handleNext();
      if (event.key === "Escape") handleClose(event);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  const handleSwipeStart = (event) => {
    touchStartRef.current = event.touches[0].clientX;
  };

  const handleSwipeMove = (event) => {
    const touchEnd = event.touches[0].clientX;
    const swipeDistance = touchStartRef.current - touchEnd;
    if (swipeDistance > 44) {
      handleNext();
      touchStartRef.current = touchEnd;
    }
    if (swipeDistance < -44) {
      handlePrev();
      touchStartRef.current = touchEnd;
    }
  };

  const downloadCurrentImage = async (event) => {
    event.stopPropagation();
    if (!activeOriginal) return;

    // iOS: share the PRELOADED file first, synchronously, so the tap gesture is preserved
    // (navigator.share must be called without an intervening network await on iOS). This
    // opens the share sheet where the user taps "Save Image" to add it to Photos.
    if (isIOSDevice && navigator.canShare) {
      const cached = preloadedShareRef.current;
      if (
        cached &&
        cached.url === activeOriginal &&
        cached.file &&
        navigator.canShare({ files: [cached.file] })
      ) {
        try {
          await navigator.share({ files: [cached.file] });
          return;
        } catch (shareErr) {
          if (shareErr && shareErr.name === "AbortError") return; // user cancelled
          // otherwise fall through to the normal download below
        }
      }
    }

    setIsDownloading(true);
    try {
      const response = await API_UTIL.post("/downloadImage", {
        imageUrl: activeOriginal,
      });

      if (response.status !== 200) {
        throw new Error("Failed to fetch image");
      }

      const link = document.createElement("a");
      link.href = response.data;
      link.download = activeFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast("Download started");
    } catch (error) {
      console.error("Error downloading image:", error);
      showToast("Download failed");
    } finally {
      setIsDownloading(false);
    }
  };

  const addToFavourite = (event) => {
    event.stopPropagation();
    const nextFavouriteState = !isFavourite;
    handleFavourite?.(currentIndex, activeOriginal, nextFavouriteState);
    setIsFavourite(nextFavouriteState);
    showToast(nextFavouriteState ? (select ? "Selected" : "Added to favourites") : (select ? "Unselected" : "Removed from favourites"));
  };

  const shareImage = async (event) => {
    event.stopPropagation();
    if (!activeOriginal) return;

    const shareUrl = new URL(activeOriginal, window.location.origin).href;

    try {
      if (navigator.share) {
        try {
          await navigator.share({
            title: "ImageShield photo",
            text: "View this ImageShield photo",
            url: shareUrl,
          });
          showToast("Share sheet opened");
          return;
        } catch (error) {
          if (error?.name === "AbortError") return;
        }
      }

      try {
        if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable");
        await navigator.clipboard.writeText(shareUrl);
      } catch {
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      showToast("Share link copied");
    } catch (error) {
      console.error("Error sharing image:", error);
      showToast("Share failed");
    }
  };

  return (
    <Overlay
      className="imageViewerOverlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) handleClose(event);
      }}
      onTouchStart={handleSwipeStart}
      onTouchMove={handleSwipeMove}
    >
      <Header>
        <HeaderLeft>
          {close && (
            <BackButton type="button" onClick={handleClose} aria-label="Back to gallery">
              <IconButton as="span" aria-hidden="true">
                <ChevronLeft />
              </IconButton>
              <span className="back-label">Gallery</span>
            </BackButton>
          )}
        </HeaderLeft>

        <HeaderCenter>
          <Counter>
            <strong>{formatNumber(hasImages ? currentIndex + 1 : 1)}</strong> / {formatNumber(hasImages ? safeImages.length : 1)}
          </Counter>
          <FileName title={activeFileName}>{activeFileName}</FileName>
        </HeaderCenter>

        <HeaderRight>
          {(favourite || select) && (
            <IconButton
              type="button"
              onClick={addToFavourite}
              $active={isFavourite}
              aria-label={isFavourite ? "Remove favourite" : "Add favourite"}
              title={isFavourite ? "Remove favourite" : "Add favourite"}
            >
              <Heart fill={isFavourite ? "currentColor" : "none"} />
            </IconButton>
          )}
          {close && (
            <IconButton type="button" onClick={handleClose} aria-label="Close viewer" title="Close">
              <X />
            </IconButton>
          )}
        </HeaderRight>
      </Header>

      <Stage>
        <Glow $hue={activeHue} />
        <FloatingArrow type="button" $side="left" onClick={handlePrev} disabled={!canGoPrev} aria-label="Previous image">
          <ChevronLeft />
        </FloatingArrow>
        {activeSrc ? (
          <ImageFrame>
            {!imageLoaded && <LoadingSpinner />}
            <ViewerImage
              src={activeSrc}
              alt={activeFileName}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageLoaded(true)}
              style={{ opacity: imageLoaded ? 1 : 0 }}
            />
            {imageLoaded && <ImageVignette />}
          </ImageFrame>
        ) : (
          <EmptyState>No image selected</EmptyState>
        )}
        <FloatingArrow type="button" $side="right" onClick={handleNext} disabled={!canGoNext} aria-label="Next image">
          <ChevronRight />
        </FloatingArrow>
      </Stage>

      {hasImages && (
        <ThumbnailRail aria-label="Image thumbnails">
          {safeImages.map((image, index) => {
            const thumbSrc = getImageSrc(image);
            return (
              <ThumbButton
                key={`${thumbSrc}-${index}`}
                type="button"
                ref={(element) => {
                  thumbRefs.current[index] = element;
                }}
                $active={index === currentIndex}
                onClick={() => goToIndex(index)}
                aria-label={`Open image ${index + 1}`}
                aria-current={index === currentIndex ? "true" : undefined}
              >
                <img src={thumbSrc} alt="" loading="lazy" />
              </ThumbButton>
            );
          })}
        </ThumbnailRail>
      )}

      <FooterBar>
        <FooterNav>
          <TextNavButton type="button" onClick={handlePrev} disabled={!canGoPrev}>
            <ChevronLeft />
            Previous
          </TextNavButton>
          <TextNavButton type="button" onClick={handleNext} disabled={!canGoNext}>
            Next
            <ChevronRight />
          </TextNavButton>
        </FooterNav>

        <FooterActions>
          <ToolbarButton
            type="button"
            onClick={downloadCurrentImage}
            disabled={isDownloading}
            aria-label={isDownloading ? "Downloading image" : "Download image"}
          >
            <ArrowDownToLine />
            <span>{isDownloading ? "Downloading" : "Download"}</span>
          </ToolbarButton>
          {sharing && (
            <ToolbarButton type="button" onClick={shareImage} aria-label="Share image">
              <Share2 />
              <span>Share</span>
            </ToolbarButton>
          )}
        </FooterActions>

        <FooterHint>
          <kbd>←</kbd>
          <kbd>→</kbd>
          <span>navigate</span>
          <kbd>ESC</kbd>
          <span>close</span>
        </FooterHint>
      </FooterBar>

      {toast && (
        <Toast role="status">
          <Check />
          {toast}
        </Toast>
      )}
    </Overlay>
  );
};

export default ImageModal;
